import type { ModuleCode } from './modules';
import { RELEASES_ARCHIVE } from './releases/archive';
import * as RELEASE_FILES from './releases/index';

/**
 * «Что нового» (БЗ-04): короткая запись после каждой выкладки — что изменилось для людей,
 * простыми словами. Новая запись — свой файл releases/<id>.ts (образец — любой файл рядом), затем
 * `node scripts/releases-index.mjs`; этот файл при новой записи не правится. Новые записи — сверху. Раздел, к которому относится запись, определяет,
 * кому она показывается: человек видит записи о разделах, которые открыты ему.
 *
 * `at` — фактическое время выкладки в UTC (с «Z»), не местное и не в будущем: страница показывает его
 * по часам читателя, а утренняя сводка берёт записи, вышедшие после прошлой сводки. Пока время не
 * наступило, страница показывает только дату.
 */
export interface Release {
  id: string;
  /** Время выкладки, ISO 8601 */
  at: string;
  title: string;
  /** Разделы портала; пустой список — запись для всех */
  modules: ModuleCode[];
  items: string[];
  /**
   * Зачем это сделано — одна-две фразы для людей: какая была проблема или чего не хватало. Без кодов требований и
   * названий файлов. Попадает в письма об обновлении; обязательно для записей с 28.10.2026.
   */
  why?: string;
  /**
   * Где посмотреть: путь страницы портала от корня, например `/site/day` или `/warehouse?tab=items`. Письма об
   * обновлении ставят по нему ссылку; обязательно для записей с 28.10.2026.
   */
  link?: string;
}

/** Путь страницы портала для ссылки в письме: от корня, без адреса сайта и без параметров маршрута ($id, :id) */
export function isPortalPath(path: string): boolean {
  return /^\/(?!\/)(?!.*\/:)[^\s$\\<>"'`\p{Cc}]*$/u.test(path) && path.length <= 300;
}

/**
 * Все записи: сначала новые из отдельных файлов (releases/<id>.ts) — по времени выкладки, свежие сверху, при равном
 * времени — по номеру; затем архив прежнего общего файла в его порядке. Каждая запись — свой файл, поэтому ветки
 * разных чатов сливаются без конфликтов; список файлов (releases/index.ts) собирает `node scripts/releases-index.mjs`.
 */
export const RELEASES: Release[] = [
  ...(Object.values(RELEASE_FILES) as Release[]).sort((a, b) =>
    a.at === b.at ? (a.id < b.id ? 1 : a.id > b.id ? -1 : 0) : a.at < b.at ? 1 : -1,
  ),
  ...RELEASES_ARCHIVE,
];

/** Запись о подробных письмах об обновлении: с её выкладки у новых записей «Что нового» обязательны why и link */
export const RELEASE_WHY_REQUIRED_FROM = '2026-10-28-update-mail-full';

/**
 * Записи, выложенные позже этой (at больше), должны говорить, зачем это сделано и где посмотреть (АД-09): why и link
 * уходят в письма об обновлении. Время — at записи RELEASE_WHY_REQUIRED_FROM, то есть фактическое время её выкладки:
 * записи, вышедшие раньше или вместе с ней, писем с «Зачем» уже не получат.
 */
export const RELEASE_WHY_REQUIRED_SINCE: string =
  RELEASES.find((r) => r.id === RELEASE_WHY_REQUIRED_FROM)?.at ?? '2026-10-28T11:26:00.000Z';
